import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { DataView } from "primereact/dataview";
import { SplitButton } from "primereact/splitbutton";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Badge } from "primereact/badge";
import { Image } from "primereact/image";
import { InstitutionalCommunication } from "../../models/institutional-communications";
import {
  useDeleteInstitutionalCommunicationMutation,
  useDuplicateInstitutionalCommunicationMutation,
  useGetInstitutionalCommunicationsQuery,
} from "../../store/services/institutional-communications";
import { InstitutionalCommunicationFilters } from "../Filters/InstitutionalCommunicationFilters";
import { useDebounce } from "../../hooks/useDebounce";

export const InstitutionalCommunicationListDV = () => {
  const navigate = useNavigate();

  const [communicationIdFilter, setCommunicationIdFilter] = useState<number>();
  const [communicationNameFilter, setCommunicationNameFilter] = useState<string>("");
  const [communicationTypeFilter, setCommunicationTypeFilter] = useState<string>();
  const [recordsLimitFilter, setRecordsLimitFilter] = useState<number>(10);
  const [communicationPendingDelete, setCommunicationPendingDelete] =
    useState<InstitutionalCommunication>();
  const debouncedCommunicationNameFilter = useDebounce(communicationNameFilter, 500);
  const effectiveCommunicationNameFilter =
    communicationNameFilter === ""
      ? ""
      : debouncedCommunicationNameFilter;

  const {
    data: institutionalCommunicationList,
    isLoading: isLoadingInstitutionalCommunicationList,
  } = useGetInstitutionalCommunicationsQuery({
    id: communicationIdFilter,
    name: effectiveCommunicationNameFilter,
    type: communicationTypeFilter,
    limit: recordsLimitFilter,
  });

  const [deleteInstitutionalCommunication, { isLoading: isDeletingCommunication }] =
    useDeleteInstitutionalCommunicationMutation();

  const [duplicateInstitutionalCommunication, { isLoading: isDuplicatingCommunication }] =
    useDuplicateInstitutionalCommunicationMutation();

  const hideDeleteDialog = () => setCommunicationPendingDelete(undefined);

  const handleDeleteCommunication = async (communicationId: number) => {
    try {
      await deleteInstitutionalCommunication(communicationId).unwrap();
      toast.success("Comunicación eliminada exitosamente");
    } catch (error) {
      toast.error("No se pudo eliminar la comunicación");
    } finally {
      hideDeleteDialog();
    }
  };

  const handleDuplicateCommunication = async (
    communicationToDuplicate: InstitutionalCommunication
  ) => {
    try {
      await duplicateInstitutionalCommunication(communicationToDuplicate.id).unwrap();
      toast.success("Comunicación duplicada exitosamente");
    } catch (error) {
      toast.error("No se pudo duplicar la comunicación");
    }
  };

  const listItems = useMemo(
    () => (Array.isArray(institutionalCommunicationList) ? institutionalCommunicationList : []),
    [institutionalCommunicationList]
  );

  return (
    <>
      <DataView
        loading={
          isLoadingInstitutionalCommunicationList ||
          isDeletingCommunication ||
          isDuplicatingCommunication
        }
        value={listItems}
        paginator
        rows={5}
        emptyMessage="Sin comunicaciones institucionales"
        header={
          <InstitutionalCommunicationFilters
            communicationId={communicationIdFilter}
            setCommunicationId={setCommunicationIdFilter}
            communicationName={communicationNameFilter}
            setCommunicationName={setCommunicationNameFilter}
            communicationType={communicationTypeFilter}
            setCommunicationType={setCommunicationTypeFilter}
            recordsLimit={recordsLimitFilter}
            setRecordsLimit={setRecordsLimitFilter}
          />
        }
        listTemplate={(communicationItems: InstitutionalCommunication[]): React.ReactNode[] =>
          communicationItems.map((institutionalCommunication, itemIndex) => (
            <div
              key={institutionalCommunication.id}
              className={`
                col-12 flex flex-column lg:flex-row align-items-center p-2 gap-4
                ${itemIndex !== 0 ? "border-top-1 surface-border" : ""}
              `}
            >
              <Image
                src={`${
                  institutionalCommunication.imageBase64
                    ? institutionalCommunication.imageBase64.startsWith("data:image/")
                      ? institutionalCommunication.imageBase64
                      : institutionalCommunication.imageBase64.startsWith("http")
                      ? institutionalCommunication.imageBase64
                      : `data:image/${
                          institutionalCommunication.imageFormat || "jpeg"
                        };base64,${institutionalCommunication.imageBase64}`
                    : institutionalCommunication.mainImageUrl
                    ? institutionalCommunication.mainImageUrl
                    : "/assets/images/Image-not-found.png"
                }`}
                height="170"
                width="auto"
                alt={institutionalCommunication.name}
              />

              <div className="flex flex-column justify-content-between align-items-start flex-1 gap-2">
                <h1 className="text-3xl font-bold text-900 p-0 m-0 flex flex-wrap align-items-center gap-2">
                  {institutionalCommunication.name}
                  <span className="text-sm text-400 font-semibold">
                    (ID: #{institutionalCommunication.id})
                  </span>
                </h1>

                <div className="flex flex-wrap gap-2">
                  <Badge value={`Tipo: ${institutionalCommunication.type || "-"}`} />
                  <Badge
                    value={`Estado: ${
                      institutionalCommunication.status === "ACTIVE" ? "Activo" : "Inactivo"
                    }`}
                    severity={
                      institutionalCommunication.status === "ACTIVE" ? "success" : "danger"
                    }
                  />
                  <Badge
                    value={`Acción: ${
                      institutionalCommunication.actionType === "EXTERNAL_URL"
                        ? "Redirección externa"
                        : "Abrir detalle"
                    }`}
                    severity="info"
                  />
                </div>

                <h2 className="text-xl font-medium text-900 p-0 m-0 white-space-nowrap overflow-hidden text-overflow-ellipsis w-12rem sm:w-30rem">
                  {institutionalCommunication.description}
                </h2>
              </div>

              <div className="flex align-items-center justify-content-center">
                <SplitButton
                  label="Acciones"
                  onClick={() =>
                    navigate(
                      `/institutional-communications/${institutionalCommunication.id}/edit`
                    )
                  }
                  model={[
                    {
                      label: "Editar",
                      icon: "pi pi-pencil",
                      command: () =>
                        navigate(
                          `/institutional-communications/${institutionalCommunication.id}/edit`
                        ),
                    },
                    {
                      label: "Duplicar",
                      icon: "pi pi-clone",
                      command: () => handleDuplicateCommunication(institutionalCommunication),
                    },
                    {
                      label: "Eliminar",
                      icon: "pi pi-times",
                      command: () =>
                        setCommunicationPendingDelete(institutionalCommunication),
                    },
                  ]}
                  outlined
                />
              </div>
            </div>
          ))
        }
      />

      {communicationPendingDelete && (
        <Dialog
          header="Eliminar comunicación institucional"
          visible={!!communicationPendingDelete}
          style={{ width: "50vw" }}
          onHide={hideDeleteDialog}
          footer={
            <div>
              <Button
                severity="danger"
                label="Eliminar"
                onClick={() => handleDeleteCommunication(communicationPendingDelete.id)}
              />
              <Button
                severity="secondary"
                label="Cancelar"
                onClick={hideDeleteDialog}
              />
            </div>
          }
        >
          <h1 className="text-3xl text-red-500 m-0 p-0">
            <i className="pi pi-exclamation-triangle"></i> ¡Cuidado!
          </h1>
          <p className="m-0 p-0">
            ¿Está seguro que desea eliminar la comunicación <b>{communicationPendingDelete.name}</b>?
          </p>
        </Dialog>
      )}
    </>
  );
};
